"use client";

import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Answer prose typography — body 14px; headings 16px to match widget titles.
 * Callers: InsightsAnswer, InsightsChat, InsightsTaskSection.
 */
const headingClass =
  "mb-1.5 mt-4 text-[16px] font-semibold leading-snug tracking-tight text-foreground first:mt-0";

const components: Components = {
  h1: ({ children }) => <h1 className={headingClass}>{children}</h1>,
  h2: ({ children }) => <h2 className={headingClass}>{children}</h2>,
  h3: ({ children }) => (
    <h3 className="mb-1 mt-3 text-[16px] font-semibold leading-snug tracking-tight text-foreground first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-1 mt-3 text-[16px] font-semibold leading-snug text-foreground first:mt-0">
      {children}
    </h4>
  ),
  h5: ({ children }) => (
    <h5 className="mb-1 mt-2.5 text-[16px] font-semibold leading-snug text-foreground first:mt-0">
      {children}
    </h5>
  ),
  h6: ({ children }) => (
    <h6 className="mb-1 mt-2.5 text-[14px] font-semibold leading-snug text-muted-foreground first:mt-0">
      {children}
    </h6>
  ),
  p: ({ children }) => (
    <p className="my-0 whitespace-pre-wrap leading-[1.75] text-foreground">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-0 list-disc space-y-1.5 pl-5 marker:text-muted-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-0 list-decimal space-y-1.5 pl-5 marker:text-muted-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-0.5 leading-[1.75] text-foreground">{children}</li>,
  strong: ({ children }) => <strong className="font-medium text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic text-foreground">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-medium text-foreground underline underline-offset-2 decoration-foreground/30 hover:decoration-foreground"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = Boolean(className?.includes("language-"));
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[13px] leading-none text-foreground">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="max-h-64 overflow-auto rounded-lg bg-muted/50 px-3 py-2 font-mono text-[13px] leading-5 text-muted-foreground">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-muted-foreground">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse font-sans text-left text-[14px]">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-border px-2 py-1.5 font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border/60 px-2 py-1.5 align-top text-foreground">{children}</td>
  ),
  hr: () => <hr className="my-2.5 border-border/60" />,
};

const CAVEAT_SPLIT = /\n(?=#{1,3}\s+Caveats?\b|\*\*Caveat:\*\*)/i;

/** Insights prose via react-markdown + GFM; caveats section rendered muted. */
export function InsightsMessageText({ text }: { text: string }) {
  const splitAt = text.search(CAVEAT_SPLIT);
  const main = splitAt >= 0 ? text.slice(0, splitAt) : text;
  const caveats = splitAt >= 0 ? text.slice(splitAt).trimStart() : null;

  return (
    <div className="font-sans text-[14px] leading-[1.75] text-foreground [&_:first-child]:mt-0">
      <div className="space-y-3">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {main}
        </ReactMarkdown>
      </div>
      {caveats ? (
        <div className="mt-6 rounded-lg border border-border/50 bg-muted/25 px-3 py-2.5 font-sans text-[14px] leading-[1.75] text-foreground/80">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {caveats}
          </ReactMarkdown>
        </div>
      ) : null}
    </div>
  );
}
